/**
 * Central step type definitions for procedures
 * Single source of truth for all step types across the application
 */

// Import step type components
import TextInputStep from '../components/steps/TextInputStep';
import NumberInputStep from '../components/steps/NumberInputStep';
import FileUploadStep from '../components/steps/FileUploadStep';
import YesNoStep from '../components/steps/YesNoStep';
import MultipleChoiceStep from '../components/steps/MultipleChoiceStep';
import SignatureStep from '../components/steps/SignatureStep';
import AIImageAnalysisStep from '../components/steps/AIImageAnalysisStep';
import TableGridStep from '../components/steps/TableGridStep';
import EquipmentInspectionStep from '../components/steps/EquipmentInspectionStep';

// Import icons
import {
  TextFields as TextIcon,
  CloudUpload as FileUploadIcon,
  CheckBox as YesNoIcon,
  RadioButtonChecked as MultipleChoiceIcon,
  Draw as SignatureIcon,
  Psychology as AIIcon,
  TableChart as TableIcon,
  Pin as NumberIcon,
  FactCheck as InspectionIcon,
} from '@mui/icons-material';

/**
 * Central enum-like object defining all available step types
 * Maps step type value to its configuration
 */
export const STEP_TYPES = {
  TEXT_INPUT: {
    value: 'TEXT_INPUT',
    label: 'Text Input',
    description: 'Collect text responses',
    icon: TextIcon,
    color: 'primary',
    component: TextInputStep,
  },
  NUMBER_INPUT: {
    value: 'NUMBER_INPUT',
    label: 'Number Input',
    description: 'Collect a number, e.g. a measurement',
    icon: NumberIcon,
    color: 'primary',
    component: NumberInputStep,
  },
  FILE_UPLOAD: {
    value: 'FILE_UPLOAD',
    label: 'File Upload',
    description: 'Upload files or images',
    icon: FileUploadIcon,
    color: 'secondary',
    component: FileUploadStep,
  },
  YES_NO: {
    value: 'YES_NO',
    label: 'Yes/No',
    description: 'Binary choice question',
    icon: YesNoIcon,
    color: 'success',
    component: YesNoStep,
  },
  MULTIPLE_CHOICE: {
    value: 'MULTIPLE_CHOICE',
    label: 'Multiple Choice',
    description: 'Select from options',
    icon: MultipleChoiceIcon,
    color: 'info',
    component: MultipleChoiceStep,
  },
  SIGNATURE: {
    value: 'SIGNATURE',
    label: 'Signature',
    description: 'Digital signature capture',
    icon: SignatureIcon,
    color: 'warning',
    component: SignatureStep,
  },
  AI_IMAGE_ANALYSIS: {
    value: 'AI_IMAGE_ANALYSIS',
    label: 'AI Image Analysis',
    description: 'AI-powered image analysis',
    icon: AIIcon,
    color: 'secondary',
    component: AIImageAnalysisStep,
  },
  TABLE_GRID: {
    value: 'TABLE_GRID',
    label: 'Table Grid',
    description: 'Configurable data table with multiple input types',
    icon: TableIcon,
    color: 'success',
    component: TableGridStep,
  },
  EQUIPMENT_INSPECTION: {
    value: 'EQUIPMENT_INSPECTION',
    label: 'Equipment Inspection',
    description: 'Inspect each device of chosen types, with room, drawing and checks',
    icon: InspectionIcon,
    color: 'warning',
    component: EquipmentInspectionStep,
  },
};

/**
 * Get an array of step types for dropdowns, etc.
 * Returns array format: [{ value, label, description }, ...]
 */
export const getStepTypesList = () => {
  return Object.values(STEP_TYPES).map(({ value, label, description }) => ({
    value,
    label,
    description,
  }));
};

/**
 * Get step type configuration by value
 */
export const getStepTypeConfig = (stepTypeValue) => {
  return STEP_TYPES[stepTypeValue];
};

/**
 * Check if a step type value is valid
 */
export const isValidStepType = (stepTypeValue) => {
  return stepTypeValue in STEP_TYPES;
};

/**
 * Get all valid step type values
 */
export const getValidStepTypeValues = () => {
  return Object.keys(STEP_TYPES);
};
